import api from './api'
import * as UpChunk from '@mux/upchunk'
import { v4 as uuidv4 } from 'uuid'
import { FFmpeg } from '@ffmpeg/ffmpeg'

// Check browser compatibility
const checkBrowserCompatibility = () => {
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
  const hasSharedArrayBuffer = typeof SharedArrayBuffer !== 'undefined'
  const hasWebAssembly = typeof WebAssembly === 'object'

  if (!hasWebAssembly) {
    throw new Error('Your browser does not support WebAssembly. Please use a modern browser.')
  }

  if (!hasSharedArrayBuffer) {
    throw new Error('Your browser does not support SharedArrayBuffer. Please use a modern browser.')
  }

  if (isSafari) {
    // Check for required security headers
    const headers = new Headers()
    if (!headers.get('Cross-Origin-Opener-Policy') || !headers.get('Cross-Origin-Embedder-Policy')) {
      throw new Error('Safari requires specific security headers. Please contact the administrator.')
    }
  }

  return true
}

// FFmpeg singleton instance
let ffmpegInstance = null

const getFFmpeg = async () => {
  if (!ffmpegInstance) {
    try {
      checkBrowserCompatibility()
      ffmpegInstance = new FFmpeg({ log: true })
      await ffmpegInstance.load()
    } catch (error) {
      console.error('FFmpeg initialization failed:', error)
      throw new Error('Failed to initialize audio processing. Please try a different browser or contact support.')
    }
  }
  return ffmpegInstance
}

const audioBufferToWav = (buffer) => {
  const numChannels = buffer.numberOfChannels
  const sampleRate = buffer.sampleRate
  const format = 1 // PCM
  const bitDepth = 16

  const bytesPerSample = bitDepth / 8
  const blockAlign = numChannels * bytesPerSample

  const wav = new ArrayBuffer(44 + buffer.length * blockAlign)
  const view = new DataView(wav)

  // Write WAV header
  writeString(view, 0, 'RIFF')
  view.setUint32(4, 36 + buffer.length * blockAlign, true)
  writeString(view, 8, 'WAVE')
  writeString(view, 12, 'fmt ')
  view.setUint32(16, 16, true)
  view.setUint16(20, format, true)
  view.setUint16(22, numChannels, true)
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, sampleRate * blockAlign, true)
  view.setUint16(32, blockAlign, true)
  view.setUint16(34, bitDepth, true)
  writeString(view, 36, 'data')
  view.setUint32(40, buffer.length * blockAlign, true)

  // Write audio data
  const data = new Float32Array(buffer.length)
  const channelData = buffer.getChannelData(0)
  for (let i = 0; i < buffer.length; i++) {
    data[i] = channelData[i]
  }
  floatTo16BitPCM(view, 44, data)

  return new Uint8Array(wav)
}

const writeString = (view, offset, string) => {
  for (let i = 0; i < string.length; i++) {
    view.setUint8(offset + i, string.charCodeAt(i))
  }
}

const floatTo16BitPCM = (view, offset, input) => {
  for (let i = 0; i < input.length; i++, offset += 2) {
    const s = Math.max(-1, Math.min(1, input[i]))
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true)
  }
}

const getUploadUrl = async () => {
  try {
    const { data: uploadUrlResponse } = await api.get('/testimonial/get-upload-url')
    if (!uploadUrlResponse.success) {
      throw new Error(uploadUrlResponse.message)
    }
    return {
      url: decodeURIComponent(uploadUrlResponse.payload.url),
      id: decodeURIComponent(uploadUrlResponse.payload.id)
    }
  } catch (error) {
    throw error
  }
}

const extractAudioFromVideo = async (videoBlob) => {
  const audioContext = new (window.AudioContext || window.webkitAudioContext)()
  const arrayBuffer = await videoBlob.arrayBuffer()

  try {
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer)
    console.log('AudioBuffer:', audioBuffer)

    // Convert AudioBuffer to WAV first
    const wavData = audioBufferToWav(audioBuffer)

    // Get FFmpeg instance with error handling
    let ffmpeg
    try {
      ffmpeg = await getFFmpeg()
    } catch (error) {
      // Fallback to WAV if FFmpeg is not available
      console.warn('FFmpeg not available, falling back to WAV format:', error)
      return new Blob([wavData], { type: 'audio/wav' })
    }

    // Write the WAV file to FFmpeg's virtual filesystem
    await ffmpeg.writeFile('input.wav', wavData)

    // Run FFmpeg command to convert WAV to MP3
    await ffmpeg.exec(['-i', 'input.wav', '-codec:a', 'libmp3lame', '-qscale:a', '2', 'output.mp3'])

    // Read the result
    const data = await ffmpeg.readFile('output.mp3')

    // Clean up files from memory
    await ffmpeg.deleteFile('input.wav')
    await ffmpeg.deleteFile('output.mp3')

    // Create MP3 blob
    return new Blob([data], { type: 'audio/mp3' })
  } catch (err) {
    console.error('Failed to process audio:', err)
    throw err
  }
}

const uploadAudio = async (audioBlob, testimonialId) => {
  const formData = new FormData()
  formData.append('file', audioBlob, 'audio.mp3') // Changed extension to .mp3
  formData.append('id', testimonialId)
  formData.append('name', 'audio.mp3') // Changed name to .mp3

  try {
    const { data: response } = await api.post('/testimonial/upload-audio', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    return response
  } catch (error) {
    throw error
  }
}

export const submitTestimonial = async ({ values, recordedVideoBlob, setUploadProgress, setUploadError, formConfig }) => {
  try {
    values.id = uuidv4()
    // Step 1: Get upload URL
    const uploadObj = await getUploadUrl()

    // Convert blob to File object
    const videoFile = new File([recordedVideoBlob], 'testimonial.mp4', {
      type: 'video/mp4'
    })

    // Step 2: Upload video using UpChunk
    await new Promise((resolve, reject) => {
      const upload = UpChunk.createUpload({
        endpoint: uploadObj.url,
        file: videoFile,
        chunkSize: 5120 // 5MB chunks
      })

      upload.on('error', (err) => {
        console.error('Upload error:', err.detail)
        setUploadError(err.detail)
        reject(err)
      })

      upload.on('progress', (progress) => {
        console.log('Uploaded', progress.detail, 'percent of this file.')
        setUploadProgress(progress.detail === 100 ? 90 : progress.detail)
      })

      upload.on('success', async () => {
        console.log('Upload completed successfully')
        setUploadProgress(100)
        resolve()
      })
    })

    // Step 3: Create testimonial record
    const { data: createResponse } = await api.post('/testimonial/create', {
      id: values.id,
      uploadId: uploadObj.id,
      testimonial: values.testimonial,
      name: values.name,
      email: values.email,
      organisationId: formConfig.organisationId,
      formId: formConfig.id,
      review: values.review
    })

    if (!createResponse.success) {
      throw new Error(createResponse.message)
    }

    // Step 4: Extract and upload audio
    console.log('Extracting audio from video...')
    const audioBlob = await extractAudioFromVideo(recordedVideoBlob)
    console.log('Audio extracted, uploading...')
    const audioResponse = await uploadAudio(audioBlob, values.id)

    if (!audioResponse.success) {
      throw new Error(audioResponse.message)
    }

    // Handle successful submission
    console.log('Testimonial and audio submitted successfully:', createResponse)
    return createResponse
  } catch (error) {
    console.error('Error submitting form:', error)
    setUploadError(error.message)
    throw error
  }
}
